import { create } from 'zustand'

/**
 * UI-only call state for the phone panel: whether the full on-call page is
 * shown (vs. the minimized bar) and whether the keypad is up. Deliberately
 * per-tab — mute/hold are NOT here; those live on the session itself and
 * mirror to every tab. The call data — caller, lifecycle, duration — comes
 * from the real Telnyx session via `usePanelCall` (`#/features/mass-action`).
 */
interface PhoneCallUiState {
  /** True while the full-screen on-call view is shown (vs. the minimized bar). */
  expanded: boolean
  /** True while the on-call view shows the DTMF keypad instead of the
   *  transcript. Per-tab by design — flipping it never mirrors to other tabs. */
  keypadOpen: boolean
  /** Call id the UI state belongs to, so a new call resets it. */
  trackedId: string | null
  expand: () => void
  minimize: () => void
  toggleKeypad: () => void
  /** Reset the keypad + re-expand when the panel's active call changes. */
  track: (callId: string | null) => void
}

export const usePhoneCall = create<PhoneCallUiState>((set) => ({
  expanded: true,
  keypadOpen: false,
  trackedId: null,
  expand: () => set({ expanded: true }),
  minimize: () => set({ expanded: false }),
  toggleKeypad: () => set((state) => ({ keypadOpen: !state.keypadOpen })),
  track: (callId) =>
    set((state) =>
      state.trackedId === callId
        ? {}
        : { trackedId: callId, expanded: true, keypadOpen: false },
    ),
}))
